import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import { todayKST } from '../../utils/date';
import { useAppPermissions } from '../../hooks/useAppPermissions';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, STD_HEADER_TOP,
  stdBlackBtn, stdOutlineBtn,
} from './adminlayout/designStandard';
import MonthlyRevenueChart from './reservationrevenue/MonthlyRevenueChart';
import MonthlyRevenueTable from './reservationrevenue/MonthlyRevenueTable';
import RevenueEventTable from './reservationrevenue/RevenueEventTable';
import SummaryCards from './reservationrevenue/SummaryCards';
import { filterEvents, getTotals, monthText } from './reservationrevenue/helpers';

export default function ReservationRevenue() {
  const navigate = useNavigate();
  const { me, permCodes } = useAppPermissions();
  const canOpenReservations = me?.role === 'admin' || permCodes?.has('AIRTEL_RESERVATION_MANAGEMENT');
  const thisMonth = todayKST().slice(0, 7);
  const defaultFrom = shiftMonth(thisMonth, -2);
  const defaultTaxPeriod = getDefaultTaxPeriod();
  const [monthFrom, setMonthFrom] = useState(defaultFrom);
  const [monthTo, setMonthTo] = useState(thisMonth);
  const [selectedMonth, setSelectedMonth] = useState(thisMonth);
  const [eventFilter, setEventFilter] = useState('all');
  const [exportYear, setExportYear] = useState(defaultTaxPeriod.year);
  const [exportQuarter, setExportQuarter] = useState(defaultTaxPeriod.quarter);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState({ rows: [], events: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 디자인 표준 헤더(stdHeaderRow)용 — 스타일 분기 전용
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 720);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function load(nextFrom = monthFrom, nextTo = monthTo) {
    const [safeFrom, safeTo] = normalizeRange(nextFrom, nextTo);
    if (safeFrom !== monthFrom) setMonthFrom(safeFrom);
    if (safeTo !== monthTo) setMonthTo(safeTo);
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ month_from: safeFrom, month_to: safeTo, include_events: '1' });
    try {
      const res = await apiClient.get(`/api/admin/reservation-revenue?${params.toString()}`);
      if (res.ok && res.data?.success) {
        const nextData = res.data.data || { rows: [], events: [] };
        setData(nextData);
        const months = nextData.rows.map((row) => monthText(row.month));
        if (!months.includes(selectedMonth)) setSelectedMonth(months[0] || '');
      } else {
        setError(res.data?.message || '조회 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const summaryRows = useMemo(
    () => selectedMonth ? data.rows.filter((row) => monthText(row.month) === selectedMonth) : data.rows,
    [data.rows, selectedMonth]
  );
  const totals = useMemo(() => getTotals(summaryRows), [summaryRows]);
  const periodTotals = useMemo(() => getTotals(data.rows), [data.rows]);
  const periodRange = useMemo(() => {
    const months = data.rows.map((row) => monthText(row.month)).filter((m) => m && m !== '-').sort();
    return months.length ? { from: months[0], to: months[months.length - 1] } : null;
  }, [data.rows]);
  const visibleEvents = useMemo(
    () => filterEvents(data.events, selectedMonth, eventFilter),
    [data.events, selectedMonth, eventFilter]
  );

  function applyPreset(months) {
    const end = todayKST().slice(0, 7);
    const start = shiftMonth(end, -months + 1);
    setMonthFrom(start);
    setMonthTo(end);
    setSelectedMonth(end);
    load(start, end);
  }

  async function downloadTaxExcel() {
    setExporting(true);
    setError(null);
    const params = new URLSearchParams({
      year: String(exportYear),
      quarter: String(exportQuarter),
    });
    try {
      const result = await apiClient.download(`/api/admin/reservation-revenue/export-tax?${params.toString()}`);
      if (!result.ok) {
        const message = await result.blob.text().catch(() => '');
        setError(message || '세무사 제출 엑셀 다운로드에 실패했습니다.');
        return;
      }
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `예약수익_세무사제출_${exportYear}Q${exportQuarter}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div style={pageStyle(isMobile)}>
      {/* 헤더 — 직원 관리 표준: 텍스트 제목 + 부제(menuConfig desc) */}
      <div style={headerStyle(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>예약 수익</h2>
          <div style={stdPageSubtitle}>예약 수익 현황을 봅니다. (수익 반영일 기준 월별 집계)</div>
        </div>
        <div style={filterStyle}>
          <input type="month" value={monthFrom} onChange={(e) => setMonthFrom(e.target.value)} style={inputStyle} />
          <span style={{ color: 'var(--text-muted)' }}>~</span>
          <input type="month" value={monthTo} onChange={(e) => setMonthTo(e.target.value)} style={inputStyle} />
          <button type="button" onClick={() => load()} style={primaryBtn}>조회</button>
          <button type="button" onClick={() => applyPreset(3)} style={ghostBtn}>3개월</button>
          <button type="button" onClick={() => applyPreset(12)} style={ghostBtn}>12개월</button>
          <button type="button" onClick={() => applyPreset(24)} style={ghostBtn}>24개월</button>
          <span style={dividerStyle} />
          <input
            type="number"
            value={exportYear}
            onChange={(e) => setExportYear(e.target.value)}
            style={{ ...inputStyle, width: 82 }}
            aria-label="세무사 제출 연도"
          />
          <select
            value={exportQuarter}
            onChange={(e) => setExportQuarter(e.target.value)}
            style={inputStyle}
            aria-label="세무사 제출 분기"
          >
            <option value="1">1분기</option>
            <option value="2">2분기</option>
            <option value="3">3분기</option>
            <option value="4">4분기</option>
          </select>
          <button type="button" onClick={downloadTaxExcel} disabled={exporting} style={exportBtn}>
            {exporting ? '다운로드 중' : '세무사 제출 엑셀'}
          </button>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      <SummaryCards totals={totals} />

      <MonthlyRevenueChart
        rows={data.rows}
        loading={loading}
        selectedMonth={selectedMonth}
        onSelectMonth={setSelectedMonth}
      />

      {periodRange && (
        <div style={periodSummaryStyle}>
          <div style={periodLabelStyle}>
            기간 합산 {periodRange.from === periodRange.to ? `(${periodRange.from})` : `(${periodRange.from} ~ ${periodRange.to})`}
          </div>
          <SummaryCards totals={periodTotals} />
        </div>
      )}

      <MonthlyRevenueTable
        rows={data.rows}
        loading={loading}
        selectedMonth={selectedMonth}
        onSelectMonth={setSelectedMonth}
      />

      <RevenueEventTable
        events={visibleEvents}
        loading={loading}
        selectedMonth={selectedMonth}
        filter={eventFilter}
        onFilterChange={setEventFilter}
        onOpenTravel={canOpenReservations ? (code) => navigate(`/admin/reservations/${encodeURIComponent(code)}`) : null}
      />
    </div>
  );
}

// 데스크톱 여백은 AdminLayout(main) + STD_HEADER_TOP 가 담당 — 자체 padding 제거
// 휴대폰은 공통 틀(AdminLayout main)이 좌우 16px 을 준다 — 겹치지 않게 0 (2026-09-28)
const pageStyle = (isMobile) => ({ padding: isMobile ? '16px 0' : 0, display: 'grid', gap: 12 });
// 표준 헤더 행 — 아래 간격은 page grid gap 이 담당하므로 margin-bottom 0 으로 상쇄
const headerStyle = (isMobile) => ({ ...stdHeaderRow(isMobile), margin: isMobile ? '4px 0 0' : `${STD_HEADER_TOP}px 0 0` });
const filterStyle = { display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' };
const inputStyle = { height: 32, padding: '0 8px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg-card)', color: 'var(--text)' };
// 버튼: 주요 CTA(조회)=stdBlackBtn / 보조(기간 프리셋·엑셀)=stdOutlineBtn — 보라/파랑 primary 제거
const primaryBtn = stdBlackBtn;
const ghostBtn = stdOutlineBtn;
const exportBtn = stdOutlineBtn;
const dividerStyle = { width: 1, height: 24, background: 'var(--border)', margin: '0 2px' };
const errorStyle = { color: '#dc2626', fontSize: 13, border: '1px solid rgba(220, 38, 38, 0.25)', background: 'rgba(220, 38, 38, 0.08)', borderRadius: 6, padding: 10 };
const periodSummaryStyle = { display: 'grid', gap: 6 };
const periodLabelStyle = { color: 'var(--text-muted)', fontSize: 12, fontWeight: 800 };

function shiftMonth(month, delta) {
  const date = new Date(`${month}-01T00:00:00`);
  date.setMonth(date.getMonth() + delta);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function normalizeRange(from, to) {
  if (from && to && from > to) return [to, from];
  return [from, to];
}

function getDefaultTaxPeriod() {
  const today = todayKST();
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const currentQuarter = Math.ceil(month / 3);
  if (currentQuarter === 1) return { year: year - 1, quarter: 4 };
  return { year, quarter: currentQuarter - 1 };
}
